<template>
  <div>
    <popup-hint hint="casks">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Casks"/>
      </template>
      <template v-slot:top>
        Casks represent barrels of aging spirits.
        You can also manage cask types, which define the transformation of spirits that occur during maturation,
        by selecting Cask types from the gear menu in the upper right.
      </template>
      <template v-slot:bottom>
        Existing casks can be created individually or you can do a batch import of casks.
      </template>
      <template v-slot:actions="{close}">
        <v-btn color="primary" class="ml-4" small @click="close();$refs.import.doImport()">Import</v-btn>
        <v-btn @click="close()" :to="{name:'InventoryCasksEditNew'}" color="primary" class="ml-4" small>Create</v-btn>
      </template>
    </popup-hint>

    <v-dialog v-model="showCaskGraph" width="496">
      <component :is="graph" zoomed @close="showCaskGraph=false" :from="graphFrom" :to="graphTo" disableTo/>
    </v-dialog>
    <import-csv v-model="doImport" type="casks" :fields="importFields" importAction="casksImport" :instructions="instructions" ref="import" @refresh="$refs.table.buildList()"/>

    <purtrak-table name="casks" objectType="casks" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="casks2" v-model="selected" @list="listBuilt"
                   fields="id,number,location,tare,info,hasNotes,
                            fillDate,daysOld,fillWeight,minimumAge,fills,retired,caskLot,
                            spiritInId,spiritOutId,
                            contents {volume,litres,strength,value,alcoholName,alcoholId,lot,imported,siteId,siteName}"
                   totalFields="totalVolume,totalValue,totalLitres"
                   :totals.sync="totals"
                   :listFilters="listFilters" :parameters="listParameters"
                   @dblclick="cask => $router.push({name:'InventoryCasksTransactions',params:{caskId:cask.id}})"
                   title="Casks"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[`As of ${$formatDate(filters.asof)}`,
                                            filters.siteId!=null?siteName:'',
                                            filters.age!=0||filters.ageRange!='and older'?ageFilterName:'',
                                            filters.lot!=null?`Lot ${filters.lot}`:'',
                                            filters.fillFrom?'Filled: '+fillDateDisplay:'']}"
                   download-endpoint="casksExportCSV">
      <template v-slot:filter>
        <scan-code @code="scan" return-unmatched-code auto-close/>
        <filter-menu include-reset @reset="filters.search='';filters.siteId=null;filters.filled='1';filters.spiritId=null;filters.age=0;filters.ageRange='and older';filters.lot=null;filters.fillFrom=null;filters.fillTo=null;filters.id=null">
          <v-list-item>
            <filter-search :label="[{text:'Number',value:'number'},{text:'Contents',value:'contents.alcoholName'},{text:'Lot',value:'contents.lot'},{text:'Info',value:'info'}]" v-model="filters.search" :search-field.sync="filters.searchField" :search-display.sync="searchDisplay"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="filled" v-model="filters.filled"/>
          </v-list-item>
          <v-list-item v-if="sites.length>1">
            <v-autocomplete label="Site" dense outlined :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="filters.siteId" item-text="name" item-value="id"/>
          </v-list-item>
          <v-list-item>
            <v-container class="pa-0">
              <v-row dense>
                <v-col cols="5">
                  <decimal-number-input label="Age" prefix="" suffix="year(s)" :precision="0" v-model="filters.age"/>
                </v-col>
                <v-col cols="7">
                  <v-select :items="['only','and older','and younger']" outlined dense hide-details v-model="filters.ageRange"/>
                </v-col>
              </v-row>
            </v-container>
          </v-list-item>
          <v-list-item>
            <date-range label="Filled" :from.sync="filters.fillFrom" :to.sync="filters.fillTo" v-model="fillDateDisplay" option="All" allow-all/>
          </v-list-item>
          <v-list-item>
            <date-picker label="As of" v-model="filters.asof" date-only/>
          </v-list-item>
          <v-list-item>
            <decimal-number-input label="Est. annual angel's share" prefix="" suffix="%" :precision="1" v-model="angelsShare"
                                  @change="saveAnglesShare"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.id" close small color="primary" class="ml-1" @click:close="filters.id=null">Scanned cask</v-chip>
          <v-chip v-show="filters.asof!==today" close small class="ml-1" @click:close="filters.asof=today">As of {{filters.asof | formatDate}}</v-chip>
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">{{searchDisplay}}</v-chip>
          <v-chip v-show="filters.filled!=='1'" close small class="ml-1" @click:close="filters.filled='1'">{{filled.find(f => f.value === filters.filled).text}}</v-chip>
          <v-chip v-show="filters.siteId!=null" close small class="ml-1" @click:close="filters.siteId=null">{{siteName}}</v-chip>
          <v-chip v-show="filters.fillFrom!=null" close small class="ml-1" @click:close="filters.fillFrom=null;filters.fillTo=null">Filled: {{fillDateDisplay}}</v-chip>
          <v-chip v-show="filters.age!=0||filters.ageRange!='and older'" close small class="ml-1" @click:close="filters.age=0;filters.ageRange='and older'">{{ageFilterName}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'EquipmentTanksFillCask'}"><v-list-item-title>Fill casks</v-list-item-title></v-list-item>
          <v-list-item :to="{name:'InventoryCasksEditNew'}"><v-list-item-title>Add existing cask</v-list-item-title></v-list-item>
          <v-divider/>
          <v-list-item :to="{name:'InventoryCasksAddEmpty'}"><v-list-item-title>Add empty casks to inventory</v-list-item-title></v-list-item>
          <v-divider/>
        </template>
        <v-list-item :to="{name:'InventoryCasksTypes'}"><v-list-item-title>Cask types</v-list-item-title></v-list-item>
        <v-menu :nudge-left="220" v-if="!$vuetify.breakpoint.mobile">
          <template v-slot:activator="{on:reports}">
            <v-list-item v-on="reports">
              <v-list-item-title>Reports and graphs</v-list-item-title>
              <v-list-item-action><v-icon>mdi-chevron-right</v-icon></v-list-item-action>
            </v-list-item>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Casks',to:{name:'InventoryCasks'}}]},query:{to:'reports/production/ReportsCasksFill'}}">
              <v-list-item-title>Casks fill report</v-list-item-title>
              <v-list-item-action-text>Report</v-list-item-action-text>
            </v-list-item>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Casks',to:{name:'InventoryCasks'}}]},query:{to:'reports/production/ReportsAngelsShare'}}">
              <v-list-item-title>Angel's share report</v-list-item-title>
              <v-list-item-action-text>Report</v-list-item-action-text>
            </v-list-item>
            <v-divider/>
            <v-list-item @click="caskGraph('DashboardCasks')">
              <v-list-item-title>Casks distribution</v-list-item-title>
              <v-list-item-action-text>Graph</v-list-item-action-text>
            </v-list-item>
            <v-list-item @click="caskGraph('DashboardCaskAging')"
              ><v-list-item-title>Casks aging</v-list-item-title>
              <v-list-item-action-text>Graph</v-list-item-action-text>
            </v-list-item>
            <v-list-item @click="caskGraph('DashboardCaskingSummary')">
              <v-list-item-title>Casking rate</v-list-item-title>
              <v-list-item-action-text>Graph</v-list-item-action-text>
            </v-list-item>
            <v-list-item @click="caskGraph('DashboardCaskingHistory')">
              <v-list-item-title>Casks filling history</v-list-item-title>
              <v-list-item-action-text>Graph</v-list-item-action-text>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-divider/>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-list-item @click="doImport=true"><v-list-item-title>Import casks from CSV</v-list-item-title></v-list-item>
        </template>
        <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Casks',to:{name:'InventoryCasks'}}]},query:{to:'reports/inventory/ReportsInventoryCasksPhysical'}}"><v-list-item-title>Physical inventory worksheet</v-list-item-title></v-list-item>
        <template v-if="connectedToAccounting">
          <v-divider/>
          <v-list-item @click="sync"><v-list-item-title>Sync casks with QBO</v-list-item-title></v-list-item>
        </template>
      </template>
      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <v-menu offset-y top>
          <template v-slot:activator="{ on }">
            <v-btn color="primary" outlined x-small style="position: absolute" class="mt-5 ml-10" v-on="on">Batch commands</v-btn>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'InventoryCasksEmpty',params:{casks:selected}}"><v-list-item-title>Empty casks</v-list-item-title></v-list-item>
            <v-divider/>
            <inventory-casks-batch-edit :casks="selected" @refresh="$refs.table.buildList($event)" v-if="connectedToAccounting && values"/>
            <inventory-casks-batch-move-site :casks="selected" @refresh="$refs.table.buildList($event)" v-if="sites.length>1"/>
            <v-divider v-if="(connectedToAccounting && values) || sites.length>1"/>
            <v-list-item @click="deleteMultiple=true;showDelete=true"><v-list-item-title>Delete</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:item.number="{item,value,printing}">
        <template v-if="printing || readOnly">{{value}}</template>
        <v-hover v-else v-slot="{hover}">
          <a @click.stop="$router.push({name:'InventoryCasksEdit',params:{caskId:item.id}})"
             :class="{'text-decoration-underline':hover}">{{item.number}}</a>
        </v-hover>
      </template>
      <template v-slot:item.contents.volume="{value,item}">
        {{value | number(3)}}
        <div class="text-caption grey--text" v-if="angelsShare">&thickapprox;{{value*(1-angelsShare/100)**(item.daysOld/365) | number(3)}}</div>
      </template>
      <template v-slot:item.contents.alcoholName="{item,printing}">
        <template v-if="printing">{{item.contents.alcoholName}}</template>
        <template v-else>
          <span :class="{'green--text':item.daysOld>=item.minimumAge,'grey--text':item.contents.volume===0}">
            {{item.contents.alcoholName}}
          </span>
          <div v-if="item.contents.imported" class="text-caption grey--text">Imported</div>
        </template>
      </template>
      <template v-slot:item.contents.litres="{value}">
        {{value | number(3)}}
      </template>
      <template v-slot:item.contents.strength="{value}">
        {{value | number(2)}}%
      </template>
      <template v-slot:item.contents.value="{value}">
        {{value | currency}}
      </template>
      <template v-slot:item.daysOld="{value}">
        {{formatAge(value)}}
        <div v-if="value>365" class="text-caption grey--text">{{ value | number(0) }} days</div>
      </template>
      <template v-slot:item.fillDate="{value}">
        {{value | formatDate}}
      </template>
      <template v-slot:item.contents.lot="{item,value,printing}">
        <template v-if="printing">{{value}}</template>
        <v-hover v-else-if="item.contents.lot" v-slot="{hover}">
          <a @click.stop="$router.push({name:'ReportsView',params:{back:[{text:'Casks',to:{name:'InventoryCasks'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:item.contents.lot,forward:0}})"
            :class="{'text-decoration-underline':hover}">{{item.contents.lot}}</a>
        </v-hover>
      </template>
      <template v-slot:header.notes>
        <v-icon>mdi-history</v-icon>
      </template>
      <template v-slot:item.notes="{item}">
        <v-btn icon :color="item.hasNotes?'green':'grey lighten-2'" small @click="cask=item;showNotes=true"><v-icon>mdi-history</v-icon></v-btn>
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-menu offset-y>
          <template v-slot:activator="{on}">
            <v-btn fab color="primary" small v-on="on"><v-icon>mdi-plus</v-icon></v-btn>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'EquipmentTanksFillCask'}"><v-list-item-title>Fill casks</v-list-item-title></v-list-item>
            <v-list-item :to="{name:'InventoryCasksEditNew'}"><v-list-item-title>Add existing cask</v-list-item-title></v-list-item>
            <v-divider/>
            <v-list-item :to="{name:'InventoryCasksAddEmpty'}"><v-list-item-title>Add empty casks to inventory</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="220" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Cask operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'InventoryCasksTransactions',params:{caskId:item.id}}"><v-list-item-title>Transactions</v-list-item-title></v-list-item>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Casks',to:{name:'InventoryCasks'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:item.contents.lot,forward:0}}"><v-list-item-title>Trace lot</v-list-item-title></v-list-item>
            <v-list-item v-if="company.allowAudit && company.permissions.includes('audit')" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <template v-if="!readOnly">
              <v-divider/>
              <v-menu :nudge-left="240">
                <template v-slot:activator="{on:reduce}">
                  <v-list-item v-on="reduce">
                    <v-list-item-title>Reduce/move inventory</v-list-item-title>
                    <v-list-item-action><v-icon>mdi-chevron-right</v-icon></v-list-item-action>
                  </v-list-item>
                </template>
                <v-list dense>
                  <v-list-item :to="{name:'InventoryCasksEmpty',params:{casks:[item]}}"><v-list-item-title>Empty/reduce contents</v-list-item-title></v-list-item>
                  <v-list-item v-if="sites.length>1" :to="{name:'ExciseVesselTransferSite',params:{vesselId:item.id}}"><v-list-item-title>Move to site</v-list-item-title></v-list-item>
                  <v-divider/>
                  <v-list-item :to="{name:'ExciseVesselReduceInventory',params:{vesselId:item.id}}"><v-list-item-title>Reduce inventory</v-list-item-title></v-list-item>
                  <v-list-item :to="{name:'ExciseVesselAdjustInventory',params:{vesselId:item.id}}"><v-list-item-title>Adjust inventory</v-list-item-title></v-list-item>
                  <v-list-item :to="{name:'ExciseVesselReassignLot',params:{vesselId:item.id}}"><v-list-item-title>Reassign lot</v-list-item-title></v-list-item>
                  <v-divider/>
                  <v-list-item :to="{name:'InventoryCasksSetAge',params:{caskId:item.id}}"><v-list-item-title>Set cask age</v-list-item-title></v-list-item>
                </v-list>
              </v-menu>
              <v-divider/>
              <v-list-item @click="cask=item;showNotes=true"><v-list-item-title>Cask notes</v-list-item-title></v-list-item>
              <generate-qr-code type="Cask" :id="item.id"/>
              <v-list-item :to="{name:'InventoryCasksEdit',params:{caskId:item.id}}"><v-list-item-title>Edit cask</v-list-item-title></v-list-item>
              <v-list-item @click="cask=item;deleteMultiple=false;showDelete=true"><v-list-item-title>Delete cask</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='contents.volume'">
              {{totals.totalVolume | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='contents.litres'">
              {{totals.totalLitres | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='contents.value' && values">
              {{totals.totalValue | currency}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>
      <template v-slot:expanded-item="{ item, headers }">
        <td :colspan="headers.length">
          <v-card class="my-4 text-caption" :class="expansionPanelClass">
          <v-container>
            <v-row no-gutters>
              <v-col cols="3">
                <span class="font-weight-medium">Location:</span> {{item.location}}
              </v-col>
              <v-col cols="3">
                <span class="font-weight-medium">Empty weight:</span> {{item.tare | number(3)}} kg
              </v-col>
              <v-col cols="3" v-if="item.retired">
                <span class="font-weight-medium">Retired</span>
              </v-col>
              <v-col cols="3" v-if="item.caskLot">
                <span class="font-weight-medium">Cask lot:</span> {{item.caskLot}}
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="3">
                <span class="font-weight-medium">Filled:</span> {{item.fillDate | formatDate}}
              </v-col>
              <v-col cols="3">
                <span class="font-weight-medium">Fill weight:</span> {{item.fillWeight | number(3)}} kg
              </v-col>
              <v-col cols="3">
                <span class="font-weight-medium">Strength:</span> {{item.contents.strength | number(2)}}% alc./vol.
              </v-col>
              <v-col cols="3">
                <span class="font-weight-medium">Fills:</span> {{item.fills | number(0)}}
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <span class="font-weight-medium">Info:</span> {{item.info}}
              </v-col>
            </v-row>
          </v-container>
          </v-card>
        </td>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="deleteMultiple?'ALL selected casks':'Cask ' + cask.number" @delete="performDelete(cask.id)"/>
    <inventory-casks-notes :caskId="cask.id" v-model="showNotes" :hasNotes.sync="cask.hasNotes"/>
  </div>
</template>

<script>
import performDelete from '@/mixins/performDelete'
import googleCharts from '@/mixins/googleCharts'
import InventoryCasksBatchEdit from '@/views/inventory/casks/InventoryCasksBatchEdit'
import InventoryCasksBatchMoveSite from '@/views/inventory/casks/InventoryCasksBatchMoveSite'
import {format,add} from 'date-fns'
import generateQrCode from '@/components/GenerateQrCode'
import InventoryCasksNotes from '@/views/inventory/casks/InventoryCasksNotes'

export default {
  name: 'InventoryCasks',
  mixins: [googleCharts,performDelete],
  components: {InventoryCasksBatchMoveSite,InventoryCasksBatchEdit,generateQrCode,InventoryCasksNotes},
  data() {
    return {
      today: format(new Date(), 'yyyy-MM-dd'),
      filters: {
        search: '',
        searchField: 'number',
        asof: null,
        filled: '1',    // 1 - filled, 0 = empty, null = all
        siteId: null,
        age: 0,
        ageRange: 'and older',
        fillFrom: null,
        fillTo: null,
        id: null
      },
      angelsShare: parseInt(localStorage.getItem('angelsShare')??0),
      totals: {totalValue:0,totalVolume:0,totalLitres:0},
      searchDisplay: '',
      fillDateDisplay: '',
      spiritTypes: [{value:null}],
      cask: {},
      showNotes: false,
      showDelete: false,
      deleteMultiple: false,
      doImport: false,
      filled: [{value:null,text:'All'},{divider:true},{value:'1',text:'Filled'},{value:'0',text:'Empty (available)'},{value:'-1',text:'Empty (retired)'},{value:'-2',text:'Empty (all)'}],
      defaultColumns: [{ value: 'number', visible:true},
                       { value: 'notes', visible:true,excludeFromExport:true},
                       { value: 'contents.alcoholName', visible:true},
                       { value: 'contents.siteName' ,visible:true},
                       { value: 'daysOld', visible:true},
                       { value: 'contents.lot', visible:true},
                       { value: 'contents.volume', visible:true},
                       { value: 'contents.litres', visible:false},
                       { value: 'contents.strength', visible:false},
                       { value: 'contents.value', visible:true},
                       { value: 'location', visible:false},
                       { value: 'info', visible:false},
                       { value: 'fillDate', visible:false},
                       { value: 'fills', visible:false}],
      headers: [{ text: '#', value: 'number', width:'10%',sortable:true},
                { text: 'NOTES', value: 'notes', width:'5%',sortable:false,align:'center',print:false},
                { text: 'CONTENTS', value: 'contents.alcoholName', width:'20%',sortable:true},
                { text: 'SITE', value: 'contents.siteName', width:'20%',sortable:true, omit:() => this.sites.length <= 1},
                { text: 'AGE', value: 'daysOld', width:'10%',sortable:true},
                { text: 'LOT', value: 'contents.lot', width:'10%',sortable:true},
                { text: 'LAA', value: 'contents.volume', width:'10%',sortable:true,align:'right'},
                { text: 'LITRES', value: 'contents.litres', width:'10%',sortable:true,align:'right', omit:() => !this.trackLitres},
                { text: 'STRENGTH', value: 'contents.strength', width:'10%',sortable:false,align:'right'},
                { text: 'VALUE', value: 'contents.value', width:'10%',sortable:true,align:'right', omit:() => !this.values},
                { text: 'LOCATION', value: 'location', width:'15%',sortable:true},
                { text: 'INFO', value: 'info', width:'20%',sortable:true},
                { text: 'FILL DATE', value: 'fillDate', width:'15%',sortable:true},
                { text: 'FILLS', value: 'fills', width:'15%',sortable:true}],
      importFields: [{name:'Number',mapTo:'',mandatory:true},{name:'Spirit',mapTo:'',mandatory:true},
                     {name:'Lot',mapTo:'',mandatory:false},{name:'Volume',mapTo:'',mandatory:true},
                     {name:'Value',mapTo:'',mandatory:false},{name:'Litres',mapTo:'',mandatory:false},{name:'Fill date',mapTo:'',mandatory:false,date:true},
                     {name:'Strength',mapTo:'',mandatory:false},{name:'Fill weight',mapTo:'',mandatory:false},
                     {name:'Empty weight',mapTo:'',mandatory:false},{name:'Location',mapTo:'',mandatory:false},
                     {name:'Info',mapTo:'',mandatory:false},
                     ],
      instructions: `<ol class="mb-4">
                        <li>Number</li>
                        <li>Spirit name</li>
                        <li>Lot (optional, default is L0)</li>
                        <li>Volume (LAA)</li>
                        <li>Litres (optional, defualt is 0)</li>
                        <li>Value (optional, default is $0)</li>
                        <li>Fill date (optional, default is today)</li>
                        <li>Strength (optional, default is 0)</li>
                        <li>Fill weight (optional, default is 0 kg)</li>
                        <li>Empty weight (optional, default is 0 kg)</li>
                        <li>Location (optional)</li>
                        <li>Info (optional)</li>
                      </ol>
                      <em>Note that not all information for a cask can be imported.</em>
                      You should, therefore, check each imported cask to ensure all settings are correct.`,
      graph: null,
      graphFrom: '',
      graphTo: '',
      showCaskGraph: false
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    connectedToAccounting() {
      return this.company.connectedToAccounting;
    },
    readOnly() {
      return !this.company.permissions.includes('inventory.write');
    },
    values() {
      return this.company.permissions.includes('values');
    },
    trackLitres() {
      return this.$store.getters.company.settings.trackLitres;
    },
    sites() {
      return this.$store.getters.sites(true, {B265:true,B266:true});
    },
    siteName() {
      return this.sites.find(site => site.id === this.filters.siteId)?.name;
    },
    ageFilterName() {
      return `${this.filters.age} year${this.filters.age===1?'':'s'} ${this.filters.ageRange}`;
    },
    listParameters() {
      return this.filters.asof ? [{name:'asof', value:`${this.filters.asof} 23:59:59`}] : [];
    },
    listFilters() {
      const filters = [];
      if (this.filters.id)
        filters.push({field:'id', comparator:'eq', value:this.filters.id});
      if (this.filters.search)
        filters.push({field:this.filters.searchField, comparator:this.filters.searchField === 'number' ? 'eq' : 'contains', value:this.filters.search});
      if (this.filters.filled !== null) {
        filters.push({field:'contents.volume', comparator:this.filters.filled==='1'?'ne':'eq', value:'0'});
        if (this.filters.filled === '0')
          filters.push({field:'retired', comparator:'eq', value:'false'});
        else if (this.filters.filled === '-1')
          filters.push({field:'retired', comparator:'eq', value:'true'});
      }
      if (this.filters.siteId)
        filters.push({field:'siteId', comparator:'eq', value:this.filters.siteId});
      if (this.filters.ageRange === 'only')
        filters.push({field:'daysOld', comparator:'ge', value:this.filters.age*365+''},
                     {field:'daysOld', comparator:'lt', value:(this.filters.age+1)*365+''});
      if (this.filters.ageRange === 'and older')
        filters.push({field:'daysOld', comparator:'ge', value:this.filters.age*365+''});
      if (this.filters.ageRange === 'and younger')
        filters.push({field:'daysOld', comparator:'lt', value:(this.filters.age+1)*365+''});
      if (this.filters.fillFrom)
        filters.push({field:'fillDate', comparator:'ge', value:`${this.filters.fillFrom} 00:00:00`});
      if (this.filters.fillTo)
        filters.push({field:'fillDate', comparator:'le', value:`${this.filters.fillTo} 23:59:59`});
      return filters;
    }
  },
  async created() {
    this.filters.asof = this.today;
    this.filters.age = parseInt(this.filters.age);

    this.graphTo = this.today;
    this.graphFrom = format(add(new Date(), {years:-2}), 'yyyy-MM-dd');

    this.$store.dispatch('checkTasks');
  },
  methods: {
    saveAnglesShare() {
      localStorage.setItem('angelsShare', this.angelsShare);
    },
    scan({type, id}) {
      if (type === 'Cask')
        Object.assign(this.filters, {search:'',siteId:null,empty:'1',spiritId:null,age:0,ageRange:'and older',lot:null,fillFrom:null,fillTo:null,id:null,id});
    },
    listBuilt({list}) {
      if (this.filters.id && list.edges.length === 1)
        this.$refs.table.expanded.push(list.edges[0].node);
    },
    async caskGraph(name) {
      await this.loadGoogleCharts();
      this.graph = () => import(`@/views/dashboard/${name}`);
      this.showCaskGraph = true;
    },
    formatAge(days) {
      if (days === 0)
        return '0 days';
      if (days/365 >= 1)
        return `${Math.trunc(days/365)} year${Math.trunc(days/365) > 1 ? 's' : ''}`;
      return `${days%365} day${days%365 > 1 ? 's' : ''}`;
    },
    async sync() {
      this.loading = true;
      const results = await this.$apiCall(`mutation InventoryCasks {
                                             accountingIntegrationSync(step:"casks") {name}
                                           }`, {});
      if (!results.error) {
        this.$store.commit('setError', {type:'success',message:`Synced casks with QBO`});
        this.$refs.table?.buildList();
      }
    }
  }
}
</script>